import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { environment } from '../../../../../../../environments/environment';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { TipoContatoEnum } from '@shared/enums/pessoa/tipo-contato.enum';
import { AcaoAlteracaoProdutoEnum } from '@shared/enums/compras/acao-alteracao-produto.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraCotacaoService } from '@shared/services/apis/compras/compra-cotacao.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { CotacaoFornecedorStruct } from '@shared/services/structs/compras/cotacao-fornecedor.struct';
import {
  CardProduct,
  CotacaoSupplier,
  initialsFromName,
  mapCotacaoFornecedor,
  mensagemAlteracaoProdutoCotacao,
  nomeFornecedorFallback,
} from '../card-edit-dialog.types';
import { CedProdutosComponent } from '../panels/ced-produtos.component';

/**
 * Etapa de Solicitação: produtos (painel compartilhado app-ced-produtos), fornecedores a cotar e
 * justificativa de criação.
 *
 * Contrato com o shell:
 * - `products` é o array do shell; o painel de produtos emite `(productsChange)` a cada alteração
 *   e a etapa repassa para o shell manter a cópia usada pelo doSave.
 * - `justificativa` é espelhada via `(justificativaChange)` — o doSave do shell a envia.
 * - `(dirty)` marca alterações não salvas (o shell é o dono do confirm de fechamento).
 * - Fornecedores usam os mesmos convites (CotacaoFornecedor) da etapa de Cotação e são gravados
 *   na hora; condição de pagamento, envio e matriz continuam exclusivos da Cotação.
 */
@Component({
  selector: 'app-ced-stage-solicitacao',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    SelectPesquisavelComponent,
    CedProdutosComponent,
  ],
  templateUrl: './ced-stage-solicitacao.component.html',
  styleUrls: ['../ced-scrollbars.css', './ced-stage-solicitacao.component.css', '../ced-painel-inclusao.css'],
})
export class CedStageSolicitacaoComponent implements OnInit, OnDestroy {
  @Input() idCompra?: number;
  @Input() products: CardProduct[] = [];
  @Input() justificativa = '';
  @Input() justificativaObrigatoria = false;

  @Output() productsChange = new EventEmitter<CardProduct[]>();
  @Output() justificativaChange = new EventEmitter<string>();
  @Output() dirty = new EventEmitter<void>();

  // Fornecedores a cotar
  fornecedores: CotacaoSupplier[] = [];
  fornecedorAdding = false;
  /** POST do convite em andamento — o select fica bloqueado até a cotação existir no servidor. */
  fornecedorAdicionando = false;
  public urlFornecedor = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Fornecedor}`;
  fornecedorControl = new FormControl<number | null>(null);
  private cotacoes: CotacaoFornecedorStruct[] = [];
  /** O backend não guarda o nome do fornecedor; cacheado por idFornecedor para reaplicar a cada mudança. */
  private nomesFornecedor = new Map<number, string>();

  private readonly subs = new Subscription();

  constructor(
    private compraCotacaoService: CompraCotacaoService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.carregarFornecedores();
  }

  /**
   * Confirmação do painel ao incluir/remover produto: o card pode ter voltado da Cotação com
   * convites já enviados ou respondidos, e salvar desativa o link enviado a eles.
   */
  avisoAlteracaoProduto = (acao: AcaoAlteracaoProdutoEnum, p: CardProduct): string | null =>
    mensagemAlteracaoProdutoCotacao(acao, p, this.cotacoes,
      c => this.nomesFornecedor.get(c.idFornecedor) || nomeFornecedorFallback(c.idFornecedor));

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ----- Fornecedores a cotar -----
  private carregarFornecedores(): void {
    if (!this.idCompra) return;

    this.subs.add(
      this.compraCotacaoService.listarCotacoes(this.idCompra).subscribe({
        next: (res) => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar os fornecedores.', AlertType.error);
            return;
          }
          this.cotacoes = res.lista || [];
          this.aplicarFornecedores();
          this.carregarNomesFornecedores();
        },
        error: () => this.alertService.show('Erro', 'Não foi possível carregar os fornecedores.', AlertType.error),
      }),
    );
  }

  private carregarNomesFornecedores(): void {
    const ids = [...new Set(this.cotacoes.map(c => c.idFornecedor).filter(Boolean))];
    if (!ids.length) return;

    this.subs.add(
      this.pessoaLookupService.getListPessoaIds({ listIdPessoa: ids }).subscribe({
        next: (res) => {
          if (res.isError) return;
          for (const p of res.listaPessoa || []) {
            this.nomesFornecedor.set(p.idPessoa, p.nome || p.nomeFantasia || nomeFornecedorFallback(p.idPessoa));
          }
          this.aplicarFornecedores();
        },
      }),
    );
  }

  private aplicarFornecedores(): void {
    this.fornecedores = this.cotacoes.map(c => {
      const base = mapCotacaoFornecedor(c);
      const name = this.nomesFornecedor.get(c.idFornecedor);
      return name ? { ...base, name, initials: initialsFromName(name) } : base;
    });
  }

  toggleAddingFornecedor(): void {
    this.fornecedorAdding = !this.fornecedorAdding;
    if (!this.fornecedorAdding) {
      this.fornecedorControl.reset();
    }
  }

  /** Grava o convite na hora, pelo mesmo endpoint da etapa de Cotação. */
  addFornecedor(sel: { id: number; descricao: string } | null): void {
    if (!sel || !this.idCompra || this.fornecedorAdicionando) return;

    if (this.fornecedores.some(s => s.idFornecedor === sel.id)) {
      this.alertService.show('Atenção', 'Este fornecedor já foi adicionado.', AlertType.warning);
      this.fornecedorControl.reset();
      return;
    }

    this.fornecedorAdicionando = true;
    this.subs.add(
      this.compraCotacaoService.adicionarCotacaoFornecedor(this.idCompra, { idFornecedor: sel.id }).subscribe({
        next: (res) => {
          this.fornecedorAdicionando = false;
          this.fornecedorControl.reset();
          if (res.isError || !res.cotacao) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível adicionar o fornecedor.', AlertType.error);
            return;
          }
          if (this.cotacoes.some(c => c.idCotacaoFornecedor === res.cotacao!.idCotacaoFornecedor)) {
            this.alertService.show('Atenção', 'Este fornecedor já foi adicionado.', AlertType.warning);
            return;
          }
          this.nomesFornecedor.set(sel.id, sel.descricao);
          this.cotacoes = [...this.cotacoes, res.cotacao];
          this.aplicarFornecedores();
          this.fornecedorAdding = false;
        },
        error: () => {
          this.fornecedorAdicionando = false;
          this.fornecedorControl.reset();
          this.alertService.show('Erro', 'Não foi possível adicionar o fornecedor.', AlertType.error);
        },
      }),
    );
  }

  removeFornecedor(s: CotacaoSupplier): void {
    if (!this.idCompra) return;

    const ref = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Remover fornecedor',
        description: `Deseja remover ${s.name} dos fornecedores a cotar? É possível adicioná-lo novamente depois.`,
        buttonConfirm: 'Remover',
        buttonClose: 'Cancelar',
      },
    });

    this.subs.add(
      ref.afterClosed().subscribe(result => {
        if (!result?.confirm || !this.idCompra) return;

        this.subs.add(
          this.compraCotacaoService.removerCotacaoFornecedor(this.idCompra, s.idCotacaoFornecedor).subscribe({
            next: (res) => {
              if (res.isError) {
                this.alertService.show('Erro', res.errorDescription || 'Não foi possível remover o fornecedor.', AlertType.error);
                return;
              }
              this.cotacoes = this.cotacoes.filter(c => c.idCotacaoFornecedor !== s.idCotacaoFornecedor);
              // Gravado na hora, como a inclusão: não suja o card (o "Descartar" não teria o que desfazer).
              this.aplicarFornecedores();
              this.alertService.show('Sucesso', 'Fornecedor removido.', AlertType.success);
            },
            error: () => this.alertService.show('Erro', 'Não foi possível remover o fornecedor.', AlertType.error),
          }),
        );
      }),
    );
  }
}
